import { useTranslation } from 'react-i18next'
import { ArrowLeft, Clock, ExternalLink, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { AuditReport } from '@/lib/api/amazon-listing'
import { useReaudit } from '@/hooks/useAmazonMutations'
import { Badge } from '@/components/v2/ui'
import { ProductScoreGrid } from './ProductScoreGrid'
import { ShareToggle } from './ShareToggle'
import { ManualSourceInputPanel } from './ManualSourceInputPanel'

interface ProductDetailHeaderProps {
  asin: string
  tld: string
  title?: string
  mainImageUrl?: string
  scores?: AuditReport['scores']
  previousScores?: Partial<AuditReport['scores']> | Record<string, number>
  jobId?: string
  productId?: string
  startedAt?: string | null
  completedAt?: string | null
  sourceType?: string
  analysisMode?: string
  sourceInput?: AuditReport['source_input']
}

function formatDuration(startedAt: string, completedAt: string): string {
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds}s`
}

const SOURCE_TYPE_VARIANT: Record<string, 'info' | 'warning' | 'secondary'> = {
  asin: 'info',
  manual: 'warning',
  channel: 'secondary',
}

export function ProductDetailHeader({
  asin, tld, title, mainImageUrl, scores, previousScores,
  jobId, productId, startedAt, completedAt, sourceType, analysisMode, sourceInput,
}: ProductDetailHeaderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const reaudit = useReaudit()
  const isManual = sourceType === 'manual'

  const handleReaudit = () => {
    if (!jobId || !window.confirm(t('amazonListing.reauditConfirm'))) return
    reaudit.mutate({ jobId }, {
      onSuccess: (data) => {
        const base = `/v2/tools/product-listing-audit/${data.audit_product_id ?? productId ?? data.job_id}`
        navigate(data.audit_product_id ? `${base}?tab=audit&audit_id=${data.job_id}` : base)
      },
    })
  }

  return (
    <div className="product-detail-header bg-gradient-to-r from-neutral-900 to-neutral-800 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <button onClick={() => navigate('/v2/tools/product-listing-audit')} className="flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">{t('common.back')}</span>
        </button>
        <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-end">
          {jobId && (
            <button
              onClick={handleReaudit}
              disabled={reaudit.isPending}
              className="flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white transition-colors"
              title={t('amazonListing.reaudit')}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${reaudit.isPending ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('amazonListing.reaudit')}</span>
            </button>
          )}
          {jobId && <ShareToggle jobId={jobId} hasRemake />}
          {!isManual && (
            <a
              href={`https://www.amazon.${tld}/dp/${asin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white"
              title="Amazon"
            >
              <span className="hidden sm:inline">Amazon</span> <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 md:gap-4">
        {mainImageUrl && (
          <img
            src={mainImageUrl}
            alt={title || asin}
            className="h-14 w-14 md:h-20 md:w-20 rounded-lg object-contain bg-white flex-shrink-0"
          />
        )}
        <div className="space-y-1.5 md:space-y-2 min-w-0">
          {title && <h1 className="text-sm md:text-lg font-semibold line-clamp-2">{title}</h1>}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            {sourceType && (
              <Badge variant={SOURCE_TYPE_VARIANT[sourceType] ?? 'default'} className="text-xs">
                {t(`amazonListing.sourceType.${sourceType}`)}
              </Badge>
            )}
            {analysisMode && (
              <Badge variant={analysisMode === 'small' ? 'warning' : 'default'} className="text-xs">
                {t(`amazonListing.analysisDepth.${analysisMode}`)}
              </Badge>
            )}
            <span className="px-2 md:px-3 py-0.5 md:py-1 bg-white/10 rounded-lg font-mono text-xs md:text-sm tracking-wider">{asin}</span>
            <span className="text-xs md:text-sm text-neutral-400 uppercase">{t(`amazonListing.marketplaces.${tld}`)}</span>
            {startedAt && completedAt && (
              <span className="flex items-center gap-1 text-xs md:text-sm text-neutral-400">
                <Clock className="h-3 w-3" />
                {formatDuration(startedAt, completedAt)}
              </span>
            )}
          </div>
        </div>
      </div>

      {scores && <ProductScoreGrid scores={scores} previousScores={previousScores} />}

      {isManual && sourceInput && <ManualSourceInputPanel sourceInput={sourceInput} />}
    </div>
  )
}
